import React, { useEffect, useState } from "react";
import { History, Car, Bike, UtensilsCrossed, Search, Download, Eye } from "lucide-react";
import { PageHeader, LoadingBlock, EmptyState } from "../../components/app/ui";
import AdminUserDialog from "../../components/admin/AdminUserDialog";
import AdminOrderDetailDialog from "../../components/admin/AdminOrderDetailDialog";
import { adminApi, fmtDateTime } from "../../lib/api";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const NameLink = ({ id, children, onOpen, testid }) =>
  id ? (
    <button onClick={() => onOpen(id)} className="text-left font-medium text-ink hover:text-brand hover:underline" data-testid={testid}>{children}</button>
  ) : (
    <span>{children}</span>
  );
const TYPES = [
  { value: "all", label: "All" },
  { value: "ride", label: "Taxi rides" },
  { value: "food", label: "Food orders" },
  { value: "private_car", label: "Private car" },
];
const TYPE_META = {
  ride: { label: "Ride", icon: Car, badge: "bg-blue-50 text-blue-700" },
  food: { label: "Food", icon: UtensilsCrossed, badge: "bg-amber-50 text-amber-700" },
  private_car: { label: "Private car", icon: Bike, badge: "bg-purple-50 text-purple-700" },
};
const STATUS_BADGE = (s) =>
  ["completed", "delivered", "settled"].includes(s) ? "bg-emerald-50 text-emerald-700"
    : ["cancelled", "rejected", "no_driver"].includes(s) ? "bg-red-50 text-red-700"
    : "bg-surface-200 text-ink-soft";

export default function AdminPastOrders() {
  const [type, setType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);

  useEffect(() => {
    setData(null);
    const t = setTimeout(() => {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      adminApi.pastOrders(type, params).then(setData).catch(() => setData({ items: [], count: 0 }));
    }, 350);
    return () => clearTimeout(t);
  }, [type, q, dateFrom, dateTo]);

  const exportCsv = () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    window.open(adminApi.pastOrdersExportUrl(type, params), "_blank");
  };

  const STATUS_GROUPS = { delivered: ["delivered", "completed", "settled"], cancelled: ["cancelled", "no_driver"], rejected: ["rejected"] };
  const filteredItems = (data?.items || []).filter((it) => statusFilter === "all" || (STATUS_GROUPS[statusFilter] || []).includes(it.status));

  return (
    <>
      <PageHeader
        title="Past orders"
        description="Completed and cancelled history across rides, food and private-car bookings."
        actions={
          <button onClick={exportCsv} data-testid="past-export-csv" className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2" data-testid="past-orders-filters">
        {TYPES.map((t) => (
          <button
            key={t.value}
            onClick={() => setType(t.value)}
            data-testid={`past-type-${t.value}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${type === t.value ? "bg-brand text-white" : "bg-surface-100 text-ink-soft hover:bg-surface-200"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="past-status-filters">
        {[{ value: "all", label: "All" }, { value: "delivered", label: "Completed" }, { value: "cancelled", label: "Cancelled" }, { value: "rejected", label: "Rejected" }].map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            data-testid={`past-status-${s.value}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${statusFilter === s.value ? "bg-ink text-surface" : "bg-white text-ink-soft ring-1 ring-surface-300 hover:text-ink"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, phone, driver, restaurant or ID…"
            data-testid="past-search"
            className="w-full rounded-full border border-surface-300 bg-white py-2 pl-9 pr-4 text-sm text-ink focus:border-brand focus:outline-none"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-ink-soft">From
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="past-date-from" className="rounded-lg border border-surface-300 bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none" />
        </label>
        <label className="flex items-center gap-2 text-xs text-ink-soft">To
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="past-date-to" className="rounded-lg border border-surface-300 bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none" />
        </label>
        {(q || dateFrom || dateTo) && (
          <button onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); }} data-testid="past-clear" className="text-xs font-semibold text-brand hover:underline">Clear</button>
        )}
      </div>

      {data === null ? (
        <LoadingBlock rows={5} />
      ) : !filteredItems.length ? (
        <EmptyState icon={History} title="No past orders" text="No orders match your filters." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-300" data-testid="past-orders-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Type</th><th className="px-4 py-3">Order #</th><th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Details</th><th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((it) => {
                const meta = TYPE_META[it.type] || TYPE_META.ride;
                const Icon = meta.icon;
                return (
                  <tr key={`${it.type}-${it.id}`} className="border-t border-surface-200" data-testid={`past-order-${it.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.badge}`}><Icon className="h-3 w-3" />{meta.label}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-soft" data-testid={`past-number-${it.id}`}>{it.order_number || "—"}</td>
                    <td className="px-4 py-3 font-medium text-ink">
                      <NameLink id={it.customer_user_id} onOpen={setProfileUserId} testid={`past-customer-${it.id}`}>{it.customer_name || "—"}</NameLink>
                    </td>
                    <td className="px-4 py-3 text-ink-soft">
                      {it.type === "food" ? <NameLink id={it.restaurant_owner_id} onOpen={setProfileUserId} testid={`past-restaurant-${it.id}`}>{it.restaurant_name || "Food order"}</NameLink> : null}
                      {it.type === "ride" ? <span className="block truncate max-w-[280px]">{it.pickup_address} → {it.drop_address}</span> : null}
                      {it.type === "private_car" ? <span className="block truncate max-w-[280px]">{it.pickup_address}{it.days ? ` · ${it.days} day(s)` : ""}</span> : null}
                      {it.driver_name ? <span className="block text-xs text-ink-muted">Driver: <NameLink id={it.driver_user_id} onOpen={setProfileUserId} testid={`past-driver-${it.id}`}>{it.driver_name}</NameLink></span> : null}
                      {it.cancelled_reason ? <span className="block text-xs text-red-600">{it.cancelled_reason}</span> : null}
                    </td>
                    <td className="px-4 py-3 font-semibold text-ink">{money(it.amount)}{it.payment_method ? <span className="ml-1 text-xs font-normal text-ink-muted">{it.payment_method === "razorpay" ? "online" : it.payment_method}</span> : null}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_BADGE(it.status)}`}>{it.status}</span></td>
                    <td className="px-4 py-3 text-ink-muted">{fmtDateTime(it.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setDetailOrder({ kind: it.type, id: it.id })} className="inline-flex items-center gap-1 rounded-full border border-surface-300 px-2.5 py-1 text-xs font-semibold text-ink-soft hover:bg-surface-100" data-testid={`past-view-${it.id}`}><Eye className="h-3 w-3" /> View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <AdminUserDialog userId={profileUserId} onClose={() => setProfileUserId(null)} />
      <AdminOrderDetailDialog order={detailOrder} onClose={() => setDetailOrder(null)} />
    </>
  );
}
